import { getOrderStore } from "../utils";
import { ExtendedAlpineComponent } from "../ExtendedAlpineComponent";
import { BillingInformationDto, CustomerInformationDto, ShippingInformationDto } from "../api";
import { SyncPayload } from "./order.store.types";
import Alpine from "alpinejs";
import { AlpineBindingContext } from "@nxtlvlsoftware/alpine-typescript/";


export class OrderContactComponent extends ExtendedAlpineComponent {
    private addressType: "Customer" | "Billing" | "Delivery";
    private storePropertyPath : string;
    private updatingFromStore: boolean = false;
    //private eventListeners: FieldListener[] = [];

    public firstName: string = "";
    public lastName: string = ""
    public email: string = "";
    public phoneNumber: string = "";
    public addressLine1: string = "";
    public addressLine2: string = "";
    public city: string = "";
    public postcode: string = "";
    public country: string = "";
    public company: string = ""
    public taxNumber: string = "";
    public marketingOptIn: boolean = false;
    public syncAddress: boolean = true;

    public focusedField: string | null = null;

    constructor(addressType: "Customer" | "Billing" | "Delivery") {
        super();
        this.addressType = addressType;

        if(this.addressType === "Customer") {
            this.storePropertyPath = "customerInformation";
        } else if (this.addressType === "Billing") {
            this.storePropertyPath = "billingInformation";
        } else if (this.addressType === "Delivery") {
            this.storePropertyPath = "shippingInformation";
        } else {
            throw new Error(`Invalid address type: ${addressType}`);
        }
    }

    init() {
        console.debug(`Initializing OrderContactComponent for address type: ${this.addressType}`);
        //this.setupEventListeners();

        const orderStore = getOrderStore();

        if(this.addressType === "Customer") {
            this.$watch('$store.order.customerInformation', (newValue) => this.updateFromStore(newValue as CustomerInformationDto));
            this.updateFromStore(orderStore.customerInformation); // Initial load
        }
        if(this.addressType === "Billing") {
            this.$watch('$store.order.billingInformation', (newValue) => this.updateFromStore(newValue as BillingInformationDto));
            this.updateFromStore(orderStore.billingInformation); // Initial load
        }
        if(this.addressType === "Delivery") {
            this.$watch('$store.order.shippingInformation', (newValue) => this.updateFromStore(newValue as ShippingInformationDto));
            this.updateFromStore(orderStore.shippingInformation); // Initial load
        }

        this.$watch('firstName', this.setupFieldWatcher('firstName').bind(this));
        this.$watch('lastName', this.setupFieldWatcher('lastName').bind(this));
        this.$watch('email', this.setupFieldWatcher('email').bind(this));
        this.$watch('phoneNumber', this.setupFieldWatcher('phoneNumber').bind(this));
        this.$watch('addressLine1', this.setupFieldWatcher('addressLine1').bind(this));
        this.$watch('addressLine2', this.setupFieldWatcher('addressLine2').bind(this));
        this.$watch('city', this.setupFieldWatcher('city').bind(this));
        this.$watch('postcode', this.setupFieldWatcher('postcode').bind(this));
        this.$watch('company', this.setupFieldWatcher('company').bind(this));
        this.$watch('taxNumber', this.setupFieldWatcher('taxNumber').bind(this));
        this.$watch('marketingOptIn', this.setupFieldWatcher('marketingOptIn').bind(this));
        this.$watch('syncAddress', this.setupFieldWatcher('syncAddress').bind(this));
    }

    private updateFromStore(store: CustomerInformationDto | BillingInformationDto | ShippingInformationDto) {
        if (!store) return;
        this.updatingFromStore = true;

        if (!this.isFieldFocused('firstName')) this.firstName = store.firstName || "";
        if (!this.isFieldFocused('lastName')) this.lastName = store.lastName || "";
        if (!this.isFieldFocused('email')) this.email = store.email || "";
        if (!this.isFieldFocused('phoneNumber')) this.phoneNumber = store.phoneNumber || "";
        if (!this.isFieldFocused('company')) this.company = store.company || "";

        // Only update address fields for billing and shipping information
        if (this.addressType === "Billing" || this.addressType === "Delivery") {
            const addressStore = store as BillingInformationDto | ShippingInformationDto;
            if (!this.isFieldFocused('addressLine1')) this.addressLine1 = addressStore.addressLine1 || "";
            if (!this.isFieldFocused('addressLine2')) this.addressLine2 = addressStore.addressLine2 || "";
            if (!this.isFieldFocused('city')) this.city = addressStore.city || "";
            if (!this.isFieldFocused('postcode')) this.postcode = addressStore.postcode || "";
            this.country = addressStore.country?.id || "";
            if (!this.isFieldFocused('company')) this.company = addressStore.company || "";
            if (!this.isFieldFocused('taxNumber')) this.taxNumber = addressStore.taxNumber || "";
        }

        if(this.addressType === "Delivery") {
            const deliveryAddressStore = store as ShippingInformationDto;
            this.syncAddress = deliveryAddressStore.isSameAsBilling ?? true;
        }

        if(this.addressType === "Customer") {
            const customerStore = store as CustomerInformationDto;
            this.marketingOptIn = customerStore.marketingOptIn || false;
        }
        this.$nextTick(() => {
            this.updatingFromStore = false;
        });
    }
    private setupFieldWatcher(field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'company' | 'taxNumber' | 'marketingOptIn' | 'addressLine1' | 'addressLine2' | 'city' | 'postcode' | 'country' | 'syncAddress') {
        const orderStore = getOrderStore();
        const debouncedUpdate = Alpine.debounce(() => {
                orderStore.updateCustomerInformation(this.buildSyncPayload(field), { policy: 'debounce' });
            }, 500);

        return () => {
            if(this.updatingFromStore) {
                console.debug("Skipping watcher update due to updatingFromStore flag for field:", field, this.addressType);
                return;
            };
            console.debug("triggering debounced update for field:", field, this.addressType);
            debouncedUpdate();
        }
    }

    private buildSyncPayload(field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'company' | 'taxNumber' | 'marketingOptIn' | 'addressLine1' | 'addressLine2' | 'city' | 'postcode' | 'country' | 'syncAddress'): Partial<SyncPayload> {
        const payload: Partial<SyncPayload> = {};
        let subItem: CustomerInformationDto | BillingInformationDto | ShippingInformationDto = {};

        switch(field) {
            case "firstName":
                subItem = { firstName: this.firstName };
                break;
            case "lastName":
                subItem = { lastName: this.lastName };
                break;
            case "email":
                subItem = { email: this.email };
                break;
            case "phoneNumber":
                subItem = { phoneNumber: this.phoneNumber };
                break;
            case "company":
                subItem = { company: this.company };
                break;
            case "taxNumber":
                subItem = { taxNumber: this.taxNumber };
                break;
        }

        if(this.addressType === "Customer") {
            switch(field) {
                case "marketingOptIn":
                    subItem = { marketingOptIn: this.marketingOptIn };
                    break;
            }
            payload.customerInformation = subItem as CustomerInformationDto;
        }

        if(this.addressType === "Billing" || this.addressType === "Delivery") {
            switch(field) {
                case "addressLine1":
                    subItem = { addressLine1: this.addressLine1 };
                    break;
                case "addressLine2":
                    subItem = { addressLine2: this.addressLine2 };
                    break;
                case "city":
                    subItem = { city: this.city };
                    break;
                case "postcode":
                    subItem = { postcode: this.postcode };
                    break;
                case "country":
                    subItem = { country: { id: this.country, name: "", alias: "" } };
                    break;
                case "syncAddress":
                    if(this.addressType === "Delivery") {
                        subItem = { isSameAsBilling: this.syncAddress };
                    }
                    break;
            }
            if(this.addressType === "Billing") {
                payload.billingInformation = subItem as BillingInformationDto;
            }
            if(this.addressType === "Delivery") {

                payload.shippingInformation = subItem as ShippingInformationDto;
            }
        }

        return payload;
    }
    private isFieldFocused(field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'company' | 'taxNumber' | 'addressLine1' | 'addressLine2' | 'city' | 'postcode'): boolean {
        const focused = this.focusedField == field;// && document.activeElement == this.$refs[field];
        if(focused) {
            console.debug(`Blocking update of field "${field}" from store because it is currently focused:`, focused, this.addressType);
        }
        return focused;
    }


    /* Alpine Bindings */

    protected setupFieldBindings<HiddenKeys extends string = ''>(field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'company' | 'taxNumber' | 'marketingOptIn' | 'addressLine1' | 'addressLine2' | 'city' | 'postcode' | 'country')
        :AlpineBindingContext<this, keyof this, HiddenKeys> & ThisType<this> {
        return {
            [":aria-invalid"]() { return getOrderStore().isInvalid(`${this.storePropertyPath}.${field}`) ? 'true' : 'false'; },
            [":aria-errormessage"]() { return getOrderStore().firstErrorFor(`${this.storePropertyPath}.${field}`) ?? ""; },
            ["@input"]() {
                const orderStore = getOrderStore();
                this.$nextTick(()=>{
                    if(orderStore.isInvalid(field)) {
                        orderStore.validateField(field);
                    }
                });
            },
            ["@focus"]() {
                this.focusedField = field;
            },
            ["@blur"]() {
                this.focusedField = null;
                const orderStore = getOrderStore();
                orderStore.updateCustomerInformation(this.buildSyncPayload(field), { policy: 'blur' });
                this.$nextTick(() => {
                    orderStore.validateField(`${this.storePropertyPath}.${field}`);
                });
            },
            ["x-ref"]: field,
            ["x-model"]: field
        };
    }
    protected setupValidationBindings(field: 'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'company' | 'taxNumber' | 'addressLine1' | 'addressLine2' | 'city' | 'postcode')
        :AlpineBindingContext<this, keyof this, ''> & ThisType<this> {
        const orderStore = getOrderStore();
        return {
            ["x-show"]() { return orderStore.isInvalid(`${this.storePropertyPath}.${field}`) },
            ["x-text"]() { return orderStore.firstErrorFor(`${this.storePropertyPath}.${field}`) }
        };
    }

    protected inputFirstName:any = this.binding({ ...this.setupFieldBindings('firstName'), ...{
        [":placeholder"]() { return "First Name"; }
    }});
    protected inputLastName:any = this.binding({ ...this.setupFieldBindings('lastName'), ...{
        [":placeholder"]() { return "Last Name (optional)"; }
    }});
    protected inputEmail:any = this.binding({ ...this.setupFieldBindings('email'), ...{
        [":placeholder"]() { return "Email Address"; }
    }});
    protected inputPhoneNumber:any = this.binding({ ...this.setupFieldBindings('phoneNumber'), ...{
        [":placeholder"]() { return "Telephone"; }
    }});
    protected inputAddressLine1:any = this.binding({ ...this.setupFieldBindings('addressLine1'), ...{
        [":placeholder"]() { return "Address Line 1"; }
    }});
    protected inputAddressLine2:any = this.binding({ ...this.setupFieldBindings('addressLine2'), ...{
        [":placeholder"]() { return "Address Line 2 (optional)"; }
    }});
    protected inputCity:any = this.binding({ ...this.setupFieldBindings('city'), ...{
        [":placeholder"]() { return "City (optional)"; }
    }});
    protected inputPostcode:any = this.binding({ ...this.setupFieldBindings('postcode'), ...{
        [":placeholder"]() { return "Postcode"; }
    }});
    protected inputCountry = this.binding({
        [":aria-invalid"]() { return getOrderStore().isInvalid(`${this.storePropertyPath}.country`) ? 'true' : 'false'; },
        [":aria-errormessage"]() { return getOrderStore().firstErrorFor(`${this.storePropertyPath}.country`) ?? ""; },
        [":placeholder"]() { return "Select a country"; },
        ["x-model"]: "country"
    });
    protected inputCompany:any = this.binding({ ...this.setupFieldBindings('company'), ...{
        [":placeholder"]() { return "Company Name (optional)"; }
    }});
    protected inputTaxNumber:any = this.binding({ ...this.setupFieldBindings('taxNumber'), ...{
        [":placeholder"]() { return "Company Tax Code (optional)"; }
    }});

    protected validationFirstName = this.setupValidationBindings('firstName');
    protected validationLastName = this.setupValidationBindings('lastName');
    protected validationEmail = this.setupValidationBindings('email');
    protected validationPhoneNumber = this.setupValidationBindings('phoneNumber');
    protected validationAddressLine1 = this.setupValidationBindings('addressLine1');
    protected validationAddressLine2 = this.setupValidationBindings('addressLine2');
    protected validationCity = this.setupValidationBindings('city');
    protected validationPostcode = this.setupValidationBindings('postcode');
    protected validationCompany = this.setupValidationBindings('company');
    protected validationTaxNumber = this.setupValidationBindings('taxNumber');
}